import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { NativeModules, Platform, useWindowDimensions } from 'react-native';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';

import { readWebKeyboard, webLayoutHeight } from '../keyboard/keyboardFrame';

/**
 * Shared viewport model used by responsive layout hooks.
 *
 * `width` / `height` represent the app's usable viewport dimensions.
 * On web, they follow `window.visualViewport` when available so the UI reacts
 * to browser chrome changes more accurately - but not to the on-screen keyboard,
 * which covers the app as it does on native rather than shrinking it (see
 * `utils/keyboard`).
 *
 * `windowWidth` / `windowHeight` mirror the rounded base viewport used to derive
 * the public dimensions.
 *
 * `insets` are the rounded safe-area inset values from
 * `react-native-safe-area-context`.
 */
type SafeViewport = {
	width: number;
	height: number;
	windowWidth: number;
	windowHeight: number;
	insets: EdgeInsets;
};

const SafeViewportContext = createContext<SafeViewport | null>(null);

type ViewportSize = {
	width: number;
	height: number;
};

type DisplayCutoutModule = {
	getTopInset: () => Promise<number>;
};

const useAndroidDisplayCutoutInset = (windowWidth: number, windowHeight: number) => {
	const [topInset, setTopInset] = useState(0);

	useEffect(() => {
		if (Platform.OS !== 'android') return;

		let mounted = true;
		const displayCutout = NativeModules.DisplayCutout as DisplayCutoutModule | undefined;

		if (!displayCutout) return;

		displayCutout
			.getTopInset()
			.then(inset => {
				if (mounted) setTopInset(Math.max(0, Math.floor(inset)));
			})
			.catch(() => {
				if (mounted) setTopInset(0);
			});

		return () => {
			mounted = false;
		};
	}, [windowHeight, windowWidth]);

	return topInset;
};

/**
 * Reads the current web viewport size.
 *
 * Web uses `visualViewport` when present because it tracks the visible area more
 * reliably than `window.innerWidth/innerHeight` when browser UI or keyboards are shown.
 * Native platforms just return the provided fallback dimensions.
 */
const getWebVisualViewportSize = (fallbackWidth: number, fallbackHeight: number): ViewportSize => {
	if (Platform.OS !== 'web' || typeof window === 'undefined') {
		return { width: fallbackWidth, height: fallbackHeight };
	}

	const visualViewport = window.visualViewport;
	// The keyboard shrinks the visual viewport too. Rescaling and re-centring every screen and
	// modal for it made them jump; it is left to cover the bottom, as on native, and whatever has
	// a focused field moves that field clear of it.
	const keyboardOpen = readWebKeyboard().height > 0;

	return {
		width: visualViewport?.width ?? window.innerWidth ?? fallbackWidth,
		height:
			(keyboardOpen ? webLayoutHeight() : visualViewport?.height) ??
			window.innerHeight ??
			fallbackHeight,
	};
};

/**
 * Subscribes to web viewport changes and keeps a stable cross-platform viewport size.
 *
 * On native, this simply returns the fallback dimensions from `useWindowDimensions()`.
 */
const useWebVisualViewportSize = (fallbackWidth: number, fallbackHeight: number): ViewportSize => {
	const [viewportSize, setViewportSize] = useState(() =>
		getWebVisualViewportSize(fallbackWidth, fallbackHeight),
	);

	useEffect(() => {
		// Native never reads this state (see the return below), and setting it here - a new object
		// every time - re-rendered every component using the viewport straight after it mounted:
		// a second full render of each screen, header and footer on every navigation.
		if (Platform.OS !== 'web') return;

		if (typeof window === 'undefined') {
			setViewportSize({ width: fallbackWidth, height: fallbackHeight });
			return;
		}

		const updateViewportSize = () => {
			const next = getWebVisualViewportSize(fallbackWidth, fallbackHeight);
			setViewportSize(current =>
				current.width === next.width && current.height === next.height ? current : next,
			);
		};

		const visualViewport = window.visualViewport;

		updateViewportSize();
		visualViewport?.addEventListener('resize', updateViewportSize);
		window.addEventListener('resize', updateViewportSize);
		window.addEventListener('orientationchange', updateViewportSize);

		return () => {
			visualViewport?.removeEventListener('resize', updateViewportSize);
			window.removeEventListener('resize', updateViewportSize);
			window.removeEventListener('orientationchange', updateViewportSize);
		};
	}, [fallbackHeight, fallbackWidth]);

	return Platform.OS === 'web' ? viewportSize : { width: fallbackWidth, height: fallbackHeight };
};

/**
 * Builds the viewport object consumed throughout the app.
 *
 * Dimensions and inset values are rounded intentionally to reduce sub-pixel layout
 * artifacts that were visible in responsive screens and overlays.
 */
const useComputedSafeViewport = (): SafeViewport => {
	const { width: windowWidth, height: windowHeight } = useWindowDimensions();
	const visualViewportSize = useWebVisualViewportSize(windowWidth, windowHeight);
	const insets = useSafeAreaInsets();
	const androidDisplayCutoutInset = useAndroidDisplayCutoutInset(windowWidth, windowHeight);

	return useMemo(() => {
		// Mixed rounding avoids the sub-pixel artifacts seen in responsive layouts.
		const roundedWidth = Math.floor(visualViewportSize.width);
		const roundedHeight = Math.ceil(visualViewportSize.height);

		const fInsets = {
			top: Platform.OS === 'android' ? androidDisplayCutoutInset : Math.floor(insets.top),
			bottom: Math.floor(insets.bottom),
			left: Math.floor(insets.left),
			right: Math.floor(insets.right),
		};

		return {
			width: Math.max(1, roundedWidth),
			height: Math.max(1, roundedHeight),
			windowWidth: Math.max(1, roundedWidth),
			windowHeight: Math.max(1, roundedHeight),
			insets: fInsets,
		};
	}, [androidDisplayCutoutInset, insets, visualViewportSize.height, visualViewportSize.width]);
};

/**
 * Provides the computed viewport to descendants so layouts can reuse one shared value.
 */
export const SafeViewportProvider = ({ children }: { children: React.ReactNode }) => {
	const viewport = useComputedSafeViewport();

	return <SafeViewportContext.Provider value={viewport}>{children}</SafeViewportContext.Provider>;
};

/**
 * Returns the app's shared viewport dimensions and safe-area insets.
 *
 * Prefer this over calling `useWindowDimensions()` and `useSafeAreaInsets()` separately
 * when building responsive UI. If no provider is mounted yet, it falls back to a local
 * computed value so the hook remains safe to call.
 */
export const useSafeViewport = () => {
	const viewport = useContext(SafeViewportContext);
	const fallbackViewport = useComputedSafeViewport();

	return viewport ?? fallbackViewport;
};
